import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
  MatDialogModule,
} from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  Firestore,
  doc,
  getDoc,
  updateDoc,
  setDoc,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  uploadBytesResumable,
} from '@angular/fire/storage';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { RealtimeBookingService } from 'src/app/shared/services/realtime-booking.service';
import { DatePipe, DecimalPipe } from '@angular/common';
import { RejectionReasonDialogComponent } from '../rejection-reason-dialog/rejection-reason-dialog.component';
import { QuotationHeaderComponent } from 'src/app/shared/document-branding/quotation-header/quotation-header.component';
import { QuotationFooterComponent } from 'src/app/shared/document-branding/quotation-footer/quotation-footer.component';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { Functions, httpsCallable } from '@angular/fire/functions';

// PDFMake Imports
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';

// html2canvas and jsPDF for backup or specific use cases
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const fonts = pdfFonts as any;
(pdfMake as any).vfs =
  fonts.pdfMake?.vfs ||
  fonts.vfs ||
  fonts.default?.pdfMake?.vfs ||
  fonts.default?.vfs;

/** Shape of the digital signature object stored on the booking & shown in the seal UI. */
export interface DigitalSignature {
  signedBy: string;   // vendor display name / company
  signedAt: string;   // ISO date string
  hash: string;       // short integrity hash (SHA-256 first 16 hex chars)
  bookingId: string;
  refNo: string;
}

@Component({
  selector: 'app-quotation-viewer-dialog',
  templateUrl: './quotation-viewer-dialog.component.html',
  styleUrls: ['./quotation-viewer-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatTabsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatTooltipModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    QuotationHeaderComponent,
    QuotationFooterComponent,
  ],
  providers: [DatePipe, DecimalPipe],
})
export class QuotationViewerDialogComponent implements OnInit, OnDestroy {
  vendorDetails: any = null;
  customerDetails: any = null;
  duration: string = '';
  quotationDate: Date = new Date();
  isVendor = false;
  isSample = false;

  @ViewChild('sigCanvas') sigCanvas!: ElementRef<HTMLCanvasElement>;
  private ctx!: CanvasRenderingContext2D;
  private isDrawing = false;
  selectedTabIndex = 0;

  deliveryCharge: number = 0;
  totalAmount: number = 0;
  vatRate: number = 5;

  // Equipment list + additional charges (mirroring v2 for visual consistency)
  equipments: any[] = [];
  additionalChargesList: any[] = [];
  termsAndConditions: string = '';

  isSaving = false;
  private rtSub?: any;

  editingTotal = false;
  editingDelivery = false;

  isLoading = true;
  showInitialChoice = false;
  showUploadSection = false;

  // Upload related
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  selectedFile: File | null = null;
  uploadPreviewUrl: string | null = null;
  uploadProgress = 0;

  /** Populated when a quotation is generated or loaded from Firestore. */
  digitalSignature: DigitalSignature | null = null;

  /** True once the vendor ticks the declaration checkbox. */
  consentGiven = false;

  /** Saved record of the consent — persisted to Firestore and shown on print. */
  consentRecord: { vendorName: string; agreedAt: string } | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { booking: any; isVendor?: boolean },
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<QuotationViewerDialogComponent>,
    private afs: Firestore,
    private storage: Storage,
    private ux: UxserviceService,
    private rtBooking: RealtimeBookingService,
    private notificationService: NotificationService,
    private auth: Auth,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private functions: Functions,
  ) { }

  safeResourceUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  safeImageUrl(url: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  async ngOnInit() {
    this.isVendor = !!this.data.isVendor;
    this.isSample = (this.data as any).isSample || false;

    if (this.data.booking && this.data.booking.quotationDate) {
      this.quotationDate =
        this.getBookingDate(this.data.booking.quotationDate) || new Date();
    }

    try {
      this.isLoading = true;
      await Promise.all([this.loadVendorDetails(), this.loadCustomerDetails()]);
    } catch (error) {
      console.error('Error loading Quotation details:', error);
    } finally {
      this.isLoading = false;
    }

    const DEFAULT_TERMS =
      'Rental period and rates are as specified above.\nQuotation is valid for 7 days from the date of issue.\nEquipment availability is subject to prior bookings.';

    // Initialize equipment list
    if (this.data.booking.equipments && this.data.booking.equipments.length > 0) {
      this.equipments = [...this.data.booking.equipments];
    } else if (this.data.booking.equipment) {
      this.equipments = [this.data.booking.equipment];
    } else {
      this.equipments = [];
    }

    // Initialize charges from booking if they exist
    if (this.data.booking.quotation) {
      this.deliveryCharge =
        Number(this.data.booking.quotation.deliveryCharge) || 0;
      this.totalAmount =
        Number(this.data.booking.quotation.totalAmount) ||
        Number(this.data.booking.totalAmount) ||
        0;
      this.additionalChargesList = this.data.booking.quotation.additionalChargesList || [];
      this.termsAndConditions = this.data.booking.quotation.termsAndConditions || DEFAULT_TERMS;
      this.vatRate = Number(this.data.booking.quotation.vatRate) || Number(this.data.booking.vatRate) || 5;
    } else {
      this.deliveryCharge = Number(this.data.booking.deliveryCharge) || 0;
      this.totalAmount = Number(this.data.booking.totalAmount) || 0;
      this.additionalChargesList = [];
      this.termsAndConditions = DEFAULT_TERMS;
      this.vatRate = Number(this.data.booking.vatRate) || 5;
    }

    this.calculateDuration();
    this.initRealtimeSync();

    // Restore existing digital signature if already saved on the booking
    if (this.data.booking.quotation?.digitalSignature) {
      this.digitalSignature = this.data.booking.quotation.digitalSignature;
    }

    // Restore consent record if the quotation was already sent
    if (this.data.booking.quotation?.consentRecord) {
      this.consentRecord = this.data.booking.quotation.consentRecord;
      this.consentGiven = true; // already agreed
    }

    // Determine if we should show initial choice (Upload vs Generate)
    if (
      this.isVendor &&
      !this.isSample &&
      !this.data.booking.quotationGenerated &&
      !this.data.booking.QUOTATION
    ) {
      this.showInitialChoice = true;
    }
  }

  // ─────────────────────────────────────────────
  //  CONSENT TOGGLE
  // ─────────────────────────────────────────────

  toggleConsent() {
    if (this.data.booking.quotationGenerated) return; // lock after sent
    this.consentGiven = !this.consentGiven;
  }

  // ─────────────────────────────────────────────
  //  DIGITAL SIGNATURE HELPERS
  // ─────────────────────────────────────────────

  private async generateIntegrityHash(payload: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(payload);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const fullHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return `${fullHex.slice(0, 4)}-${fullHex.slice(4, 8)}-${fullHex.slice(8, 12)}-${fullHex.slice(12, 16)}`.toUpperCase();
  }

  async buildDigitalSignature(): Promise<DigitalSignature> {
    const now = new Date();
    const vendorId =
      this.data.booking.equipment?.bywhom || this.data.booking.vendorId || 'unknown';

    const payload = [
      this.data.booking.id,
      vendorId,
      this.totalAmount,
      this.deliveryCharge,
      this.getFinalTotal(),
      now.toISOString(),
    ].join('|');

    const hash = await this.generateIntegrityHash(payload);

    return {
      signedBy: this.vendorDetails?.companyName || this.vendorDetails?.name || 'Vendor',
      signedAt: now.toISOString(),
      hash,
      bookingId: this.data.booking.id,
      refNo: 'QT-' + this.data.booking.id.slice(-6).toUpperCase(),
    };
  }

  // ─────────────────────────────────────────────
  //  LIFECYCLE / REALTIME SYNC
  // ─────────────────────────────────────────────

  getBookingDate(field: any): Date | null {
    if (!field) return null;
    if (typeof field.toDate === 'function') {
      return field.toDate();
    }
    if (field instanceof Date) {
      return field;
    }
    const d = new Date(field);
    return isNaN(d.getTime()) ? null : d;
  }

  initRealtimeSync() {
    this.rtSub = this.rtBooking
      .watchBooking(this.data.booking.id)
      .subscribe((rtData: any) => {
        if (rtData) {
          if (rtData.status) this.data.booking.status = rtData.status;
          if (rtData.quotation) {
            const q = rtData.quotation;
            this.data.booking.quotationGenerated = q.generated;
            this.data.booking.quotationApproved = q.approved;
            this.data.booking.quotationRejected = !!q.rejected;
            this.data.booking.rejectionReason = q.rejectionReason || '';
            if (q.url) this.data.booking.QUOTATION = q.url;

            if (q.vendorSignature) {
              if (!this.vendorDetails) this.vendorDetails = {};
              this.vendorDetails.signatureUrl = q.vendorSignature;
            }
            if (q.vendorSignatureBase64) {
              if (!this.vendorDetails) this.vendorDetails = {};
              this.vendorDetails.signatureBase64 = q.vendorSignatureBase64;
            }
            if (q.deliveryCharge !== undefined) {
              this.deliveryCharge = q.deliveryCharge;
            }
            if (q.baseAmount !== undefined) {
              this.totalAmount = q.baseAmount;
            }
            if (q.digitalSignature) {
              this.digitalSignature = q.digitalSignature;
            }
          }
          this.cdr.detectChanges();
        }
      });
  }

  ngOnDestroy() {
    this.rtSub?.unsubscribe();
  }

  // ─────────────────────────────────────────────
  //  VENDOR / CUSTOMER DATA
  // ─────────────────────────────────────────────

  async loadVendorDetails() {
    const vendorId =
      this.data.booking.equipment?.bywhom || this.data.booking.vendorId;
    if (!vendorId) return;

    const vendorRef = doc(this.afs, 'users-list', vendorId);
    const snap = await getDoc(vendorRef);
    if (snap.exists()) {
      const u = snap.data();
      this.vendorDetails = {
        name: u['displayName'] || u['firstname'] || 'Vendor',
        companyName: u['companyName'],
        phone: u['phoneNumber'] || u['phone'],
        email: u['email'],
        address: u['companyAddress'] || u['companyDetails'] || u['address'],
        logo: u['companyLogoUrl'] || u['photoURL'],
        trn: u['trnNumber'] || u['businessLicenseNo'] || '',
        signatureUrl:
          this.data.booking.quotation?.vendorSignature || u['signatureUrl'],
        signatureBase64:
          this.data.booking.quotation?.vendorSignatureBase64 || null,
        quotationBranding: u['quotationBranding'] || null,
      };
      this.vendorDetails.letterheadUrl = u['quotationBranding']?.letterheadUrl || null;
      // Used by the HTML preview only — browsers render URLs efficiently and
      // there's no CORS/canvas involved when just displaying an <img src="...">.
      this.vendorDetails.letterheadImageUrl = u['quotationBranding']?.imageUrl || null;
      // Used by the PDF generator. Produced server-side by convertLetterhead — already
      // a complete data URI, so pdfMake can consume it with zero extra network/canvas work.
      // Backward compatible: legacy vendors without this field simply get null here,
      // and the PDF generator falls back to the white template (see hasLetterhead below).
      this.vendorDetails.letterheadBase64 = u['quotationBranding']?.letterheadBase64 || null;
    }
  }

  async loadCustomerDetails() {
    if (this.data.booking.isVendorOwnBooking) {
      this.customerDetails = {
        companyName: this.data.booking.quotation?.customerDetails?.companyName || this.data.booking.customer?.companyName || 'Customer',
        companyDetails: this.data.booking.quotation?.customerDetails?.companyDetails || this.data.booking.customer?.companyAddress || this.data.booking.customer?.location || '',
        phoneNumber: this.data.booking.quotation?.customerDetails?.phoneNumber || this.data.booking.customer?.phone || '',
        email: this.data.booking.quotation?.customerDetails?.email || this.data.booking.customer?.email || '',
        logoUrl: null,
      };
      return;
    }

    const customerId = this.data.booking.bywhom || this.data.booking.customerId;
    if (!customerId) return;

    const custRef = doc(this.afs, 'users-list', customerId);
    const snap = await getDoc(custRef);
    if (snap.exists()) {
      const u = snap.data();
      this.customerDetails = {
        companyName: u['companyName'] || u['displayName'] || 'Customer',
        companyDetails: u['companyAddress'] || u['companyDetails'] || '',
        phoneNumber: u['phoneNumber'] || '',
        email: u['email'] || '',
        logoUrl: u['companyLogoUrl'] || null,
      };
    }
  }

  calculateDuration() {
    const booking = this.data.booking;
    if (booking.customer?.priceType === 'per_trip') {
      const rate = booking.rate || 0;
      const total = booking.totalAmount || 0;
      const trips = rate > 0 ? Math.round(total / rate) : 1;
      this.duration = `${trips} Trip${trips !== 1 ? 's' : ''}`;
      return;
    }

    const start = this.getBookingDate(
      booking.customer?.startDate || booking.startDate,
    );
    const end = this.getBookingDate(
      booking.customer?.endDate || booking.endDate,
    );

    if (!start || !end) return;
    const diff = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 3600 * 24),
    );
    this.duration = diff > 0 ? `${diff} days` : '1 day';
  }

  // ─────────────────────────────────────────────
  //  FINANCIAL CALCULATION HELPERS (v2 parity)
  // ─────────────────────────────────────────────

  getEquipmentDurationValue(eq: any): number {
    if (eq.numberOfDays !== undefined && eq.numberOfDays !== null && eq.numberOfDays !== 0) {
      return Number(eq.numberOfDays);
    }
    if (this.data.booking.customer?.priceType === 'per_trip') {
      if (eq.numberOfTrips !== undefined && eq.numberOfTrips !== null) return Number(eq.numberOfTrips);
      const rate = eq.price || this.data.booking.rate || 0;
      const total = eq.totalAmount || eq.price || this.data.booking.totalAmount || 0;
      return rate > 0 ? Math.round(total / rate) : 1;
    }
    const start = this.getBookingDate(eq.startDate || this.data.booking.customer?.startDate || this.data.booking.startDate);
    const end = this.getBookingDate(eq.endDate || this.data.booking.customer?.endDate || this.data.booking.endDate);
    if (!start || !end) return 1;
    const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24));
    return diff > 0 ? diff : 1;
  }

  getEquipmentUnit(eq: any): string {
    const type = eq.priceType || this.data.booking.customer?.priceType || 'daily';
    switch (type) {
      case 'daily': return 'Days';
      case 'weekly': return 'Weeks';
      case 'monthly': return 'Month';
      case 'per_trip': return 'Trip';
      case 'hourly': return 'Hrs';
      default:
        if (type && typeof type === 'string') {
          return type.charAt(0).toUpperCase() + type.slice(1);
        }
        return '';
    }
  }

  getEquipmentAmount(eq: any): number {
    if (eq.totalAmount) return Number(eq.totalAmount);
    if (this.data.booking.customer?.priceType === 'per_trip') {
      return Number(eq.price || this.totalAmount || 0);
    }
    if (eq.price) {
      const dur = this.getEquipmentDurationValue(eq);
      return Number(eq.price) * (dur || 1);
    }
    return Number(this.totalAmount) || 0;
  }

  getSubTotal(): number {
    if (
      this.equipments.length === 1 &&
      this.equipments[0].equipmentId === this.data.booking.equipment?.equipmentId &&
      !this.equipments[0].price
    ) {
      return Number(this.totalAmount) || 0;
    }
    return this.equipments.reduce((sum: number, eq: any) => sum + this.getEquipmentAmount(eq), 0);
  }

  getTotalAdditionalCharges(): number {
    return this.additionalChargesList.reduce((sum: number, charge: any) => sum + (Number(charge.amount) || 0), 0);
  }

  getVatRate(): number {
    return Number(this.vatRate) || 5;
  }

  getVatAmount(): number {
    const base = this.getSubTotal() + this.getTotalAdditionalCharges();
    return base * (this.getVatRate() / 100);
  }

  getFinalTotalWithVat(): number {
    const base = this.getSubTotal() + this.getTotalAdditionalCharges();
    return base + this.getVatAmount();
  }

  getFinalTotal(): number {
    return (Number(this.totalAmount) || 0) + (Number(this.deliveryCharge) || 0);
  }

  // ─────────────────────────────────────────────
  //  NAVIGATION
  // ─────────────────────────────────────────────

  onGenerateClick() {
    this.showInitialChoice = false;
  }

  openUploadQuotationDialog() {
    this.showUploadSection = true;
    this.showInitialChoice = false;
  }

  onBackToChoice() {
    this.showUploadSection = false;
    this.showInitialChoice = true;
    this.selectedFile = null;
    this.uploadPreviewUrl = null;
  }

  openFilePicker() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    if (!(file.type.includes('pdf') || file.type.includes('image'))) {
      this.ux.showError('Please select a PDF or image file.');
      return;
    }

    this.selectedFile = file;
    this.uploadPreviewUrl = URL.createObjectURL(file);
  }

  // ─────────────────────────────────────────────
  //  UPLOAD QUOTATION (physical doc)
  // ─────────────────────────────────────────────

  async uploadQuotation() {
    if (!this.selectedFile) {
      this.ux.showError('Please select a file to upload.');
      return;
    }

    this.isSaving = true;
    try {
      const filePath = `Quotations/${this.data.booking.id}/${Date.now()}_${this.selectedFile.name}`;
      const fileRef = ref(this.storage, filePath);
      const uploadResult = await uploadBytes(fileRef, this.selectedFile);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      this.data.booking.QUOTATION = downloadURL;

      await this.rtBooking.updateQuotation(
        this.data.booking.id,
        {
          url: downloadURL,
          generated: true,
          date: new Date().toISOString(),
          approved: false,
          rejected: false,
        },
        this.auth.currentUser?.uid,
      );

      const bookingRef = doc(this.afs, 'booking-list', this.data.booking.id);
      await updateDoc(bookingRef, {
        quotationGenerated: true,
        QUOTATION: downloadURL,
        'quotation.url': downloadURL,
        'quotation.generated': true,
        'quotation.date': new Date(),
      });

      if (this.data.booking.customerId) {
        this.notificationService.sendNotification(
          this.data.booking.customerId,
          {
            title: 'Quotation Uploaded',
            message: `Vendor has uploaded a Quotation for Booking #${this.data.booking.id}`,
            type: 'success',
            link: `/booking-orders`,
            bookingId: this.data.booking.id,
          },
        );
      }

      this.ux.openSnackBar('Quotation uploaded successfully!', 'OK');
      this.showUploadSection = false;
      this.showInitialChoice = false;
    } catch (err) {
      console.error('❌ Upload error:', err);
      this.ux.showError('Failed to upload Quotation.');
    } finally {
      this.isSaving = false;
    }
  }

  isImage(url: string | undefined): boolean {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.startsWith('data:image/')) return true;
    // Decode Firebase Storage encoded paths (e.g. %2F -> /)
    const decoded = decodeURIComponent(lowerUrl.split('?')[0]);
    return (
      decoded.endsWith('.jpg') ||
      decoded.endsWith('.jpeg') ||
      decoded.endsWith('.png') ||
      decoded.endsWith('.gif') ||
      decoded.endsWith('.webp') ||
      decoded.endsWith('.bmp') ||
      decoded.endsWith('.svg')
    );
  }

  // ─────────────────────────────────────────────
  //  SAVE & SEND (digital quotation)
  // ─────────────────────────────────────────────

  async confirmSaveQuotation() {
    if (!this.consentGiven) {
      this.ux.showError('Please tick the declaration checkbox before sending.');
      return;
    }

    this.isSaving = true;
    try {
      const now = new Date();

      this.digitalSignature = await this.buildDigitalSignature();

      this.consentRecord = {
        vendorName:
          this.vendorDetails?.companyName ||
          this.vendorDetails?.name ||
          'Vendor',
        agreedAt: now.toISOString(),
      };

      const bookingRef = doc(this.afs, 'booking-list', this.data.booking.id);

      const updateData: any = {
        quotationGenerated: true,
        quotationDate: now,
        quotationRejected: false,
        'quotation.vendorSignature': this.vendorDetails?.signatureUrl || null,
        'quotation.vendorSignatureBase64':
          this.vendorDetails?.signatureBase64 || null,
        'quotation.deliveryCharge': Number(this.deliveryCharge) || 0,
        'quotation.totalAmount': Number(this.totalAmount) || 0,
        'quotation.finalTotal': this.getFinalTotal(),
        'quotation.digitalSignature': this.digitalSignature,
        'quotation.consentRecord': this.consentRecord,
        deliveryCharge: Number(this.deliveryCharge) || 0,
        totalAmount: Number(this.totalAmount) || 0,
        totalQuotationAmount: this.getFinalTotal(),
        QUOTATION: this.data.booking.QUOTATION || null,
        rejectionReason: null,
      };

      await updateDoc(bookingRef, updateData);

      await this.rtBooking.updateQuotation(
        this.data.booking.id,
        {
          generated: true,
          date: now.toISOString(),
          vendorSignature: this.vendorDetails?.signatureUrl || null,
          vendorSignatureBase64: this.vendorDetails?.signatureBase64 || null,
          deliveryCharge: Number(this.deliveryCharge) || 0,
          baseAmount: Number(this.totalAmount) || 0,
          totalAmount: this.getFinalTotal(),
          approved: false,
          rejected: false,
          rejectionReason: '',
        },
        this.auth.currentUser?.uid,
      );

      if (this.data.booking.customerId && !this.data.booking.isVendorOwnBooking) {
        this.notificationService.sendNotification(
          this.data.booking.customerId,
          {
            title: 'New Quotation Received',
            message: `Vendor has sent a quotation for Booking #${this.data.booking.id}`,
            type: 'info',
            link: '/booking-orders',
            bookingId: this.data.booking.id,
          },
        );
      }

      this.ux.showSuccess('Quotation generated and sent to customer');
      this.dialogRef.close({ saved: true });
    } catch (error) {
      console.error('Error saving Quotation:', error);
      this.ux.showError('Failed to save Quotation');
    } finally {
      this.isSaving = false;
    }
  }

  // ─────────────────────────────────────────────
  //  APPROVE / REJECT
  // ─────────────────────────────────────────────

  async approveQuotation() {
    this.isSaving = true;
    this.ux.showOverlay('Approving Quotation...');

    try {
      const bookingRef = doc(this.afs, 'booking-list', this.data.booking.id);
      const now = new Date();

      await updateDoc(bookingRef, {
        quotationApproved: true,
        quotationApprovedAt: now,
        vendorConfirmed: true,
        status: 'Confirmed',
        'quotation.deliveryCharge': Number(this.deliveryCharge) || 0,
        'quotation.totalAmount': Number(this.totalAmount) || 0,
        'quotation.finalTotal': this.getFinalTotal(),
        deliveryCharge: Number(this.deliveryCharge) || 0,
        totalAmount: Number(this.totalAmount) || 0,
        totalQuotationAmount: this.getFinalTotal(),
      });

      await this.rtBooking.updateQuotation(
        this.data.booking.id,
        {
          approved: true,
          deliveryCharge: Number(this.deliveryCharge) || 0,
          totalAmount: this.getFinalTotal(),
          baseAmount: Number(this.totalAmount) || 0,
        },
        this.auth.currentUser?.uid,
      );

      await this.rtBooking.updateStatus(
        this.data.booking.id,
        'Confirmed',
        {
          acceptedAt: now.toISOString(),
          confirmedAt: now.toISOString(),
        },
        this.auth.currentUser?.uid,
      );

      const vendorId =
        this.data.booking.equipment?.bywhom || this.data.booking.vendorId;
      if (vendorId) {
        this.notificationService.sendNotification(vendorId, {
          title: 'Quotation Approved',
          message: `Customer has approved your quotation for Booking #${this.data.booking.id}`,
          type: 'success',
          link: '/business/bookings',
          bookingId: this.data.booking.id,
        });
      }

      this.ux.showSuccess('Quotation Approved');
      this.dialogRef.close({ approved: true });
    } catch (error) {
      console.error('Error approving Quotation:', error);
      this.ux.showError('Failed to approve Quotation');
    } finally {
      this.isSaving = false;
      this.ux.closeOverlay();
    }
  }

  async rejectQuotation() {
    const dialogRef = this.dialog.open(RejectionReasonDialogComponent, {
      width: '400px',
      data: {
        title: 'Reject Quotation',
        label:
          'Please provide a reason for rejecting this quotation. This feedback will be visible to the vendor.',
        buttonText: 'Reject Quotation',
      },
    });

    const reason = await dialogRef.afterClosed().toPromise();
    if (!reason) return;

    this.isSaving = true;
    try {
      const bookingRef = doc(this.afs, 'booking-list', this.data.booking.id);
      await updateDoc(bookingRef, {
        quotationRejected: true,
        rejectionReason: reason,
        quotationGenerated: false,
        'quotation.rejected': true,
        'quotation.rejectionReason': reason,
      });

      await this.rtBooking.updateQuotation(
        this.data.booking.id,
        {
          rejected: true,
          rejectionReason: reason,
          generated: false,
        },
        this.auth.currentUser?.uid,
      );

      const vendorId =
        this.data.booking.equipment?.bywhom || this.data.booking.vendorId;
      if (vendorId) {
        this.notificationService.sendNotification(vendorId, {
          title: 'Quotation Rejected',
          message: `Customer has rejected your quotation for Booking #${this.data.booking.id}. Reason: ${reason}`,
          type: 'warning',
          link: '/business/bookings',
          bookingId: this.data.booking.id,
        });
      }

      this.ux.showSuccess('Quotation Rejected');
      this.dialogRef.close({ rejected: true, reason });
    } catch (error) {
      console.error('Error rejecting Quotation:', error);
    } finally {
      this.isSaving = false;
    }
  }

  // ─────────────────────────────────────────────
  //  PDF DOWNLOAD
  // ─────────────────────────────────────────────
  //
  // NOTE: a getLetterheadBase64() helper used to live here, downloading the
  // letterhead PNG via an HTMLImageElement + <canvas>.toDataURL(). That
  // approach is fundamentally broken for privately-served Firebase Storage
  // objects: drawImage() taints the canvas unless the response carries CORS
  // headers permitting pixel reads, so toDataURL() threw and the PDF fell
  // back to a plain white template.
  //
  // Fix: base64 generation now happens server-side in convertLetterhead,
  // which encodes the in-memory PNG buffer to a data URI at conversion time
  // (before any CORS boundary exists) and stores it on
  // quotationBranding.letterheadBase64. The browser just reads that string
  // from Firestore — no image fetch, no canvas, no CORS exposure, no extra
  // network round trip.

  private buildStandaloneHtml(element: HTMLElement): string {
    const clone = element.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('.no-print').forEach(el => el.remove());

    let globalStyles = '';
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach(el => {
      if (el.tagName.toLowerCase() === 'style') {
        globalStyles += el.innerHTML + '\n';
      }
    });

    const printResets = `
      body { margin: 0; padding: 0; font-family: 'Inter', Helvetica, Arial, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      * { box-sizing: border-box; }
      .paginated-preview-container { display: block; width: 100%; margin: 0; padding: 0; }
      .financial-table { table-layout: fixed; width: 100%; border-collapse: collapse; }
      .financial-table th, .financial-table td { box-sizing: border-box; }
      .financial-table th:nth-child(1), .financial-table td:nth-child(1) { width: 55px; text-align: center; }
      .financial-table th:nth-child(2), .financial-table td:nth-child(2) { text-align: left; }
      .financial-table th:nth-child(3), .financial-table td:nth-child(3) { width: 90px; text-align: center; }
      .financial-table th:nth-child(4), .financial-table td:nth-child(4) { width: 90px; text-align: center; }
      .financial-table th:nth-child(5), .financial-table td:nth-child(5) { width: 90px; text-align: center; }
      .financial-table th:nth-child(6), .financial-table td:nth-child(6) { width: 145px; text-align: right; }
    `;

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    ${printResets}
    ${globalStyles}
  </style>
</head>
<body>${clone.outerHTML}</body>
</html>`;
  }

  async downloadPdf() {
    // Case 1: Uploaded Custom PDF/Image file
    if (this.data.booking.QUOTATION && !this.data.booking.quotationGenerated) {
      window.open(this.data.booking.QUOTATION, '_blank');
      return;
    }

    // Case 2: Digital Generated Quotation - Use serverless PDF generator (V2 style)
    this.ux.showOverlay('Generating Quotation PDF...');
    try {
      const previewEl = document.getElementById('quotation-content');
      if (!previewEl) {
        throw new Error('Quotation preview content not found.');
      }

      this.cdr.detectChanges();
      const html = this.buildStandaloneHtml(previewEl);

      const generatePdf = httpsCallable<{ html: string; bookingId: string }, { url: string; path: string }>(
        this.functions,
        'generateQuotationPdf',
      );

      const result = await generatePdf({ html, bookingId: this.data.booking.id });
      const pdfUrl = result.data.url;
      const filename = `Quotation-${this.data.booking.id.slice(-6).toUpperCase()}.pdf`;

      try {
        const res = await fetch(pdfUrl);
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
        this.ux.showSuccess('PDF Downloaded');
      } catch (corsError) {
        console.error('PDF direct download failed:', corsError);
        window.open(pdfUrl, '_blank');
        this.ux.showSuccess('PDF opened in new tab');
      }
    } catch (e: any) {
      console.error('Puppeteer PDF Generation failed, trying client-side pdfMake:', e);
      try {
        const docDefinition = await this.getQuotationPdfDefinition();
        pdfMake
          .createPdf(docDefinition)
          .download(
            `Quotation-${this.data.booking.id.slice(-6).toUpperCase()}.pdf`,
          );
        this.ux.showSuccess('PDF Downloaded via fallback');
      } catch (pdfMakeErr) {
        console.error('pdfMake fallback failed, trying legacy:', pdfMakeErr);
        await this.downloadPdfLegacy();
      }
    } finally {
      this.ux.closeOverlay();
    }
  }

  async downloadPdfLegacy() {
    try {
      const content =
        document.getElementById('quotation-content') ||
        document.getElementById('lpo-content');
      if (!content) throw new Error('Content not found');

      const canvas = await html2canvas(content, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`Quotation-${this.data.booking.id.slice(-6).toUpperCase()}.pdf`);
    } catch (e) {
      console.error('Legacy PDF failed:', e);
      this.ux.showError('Legacy PDF Generation failed');
    }
  }

  async getQuotationPdfDefinition(): Promise<TDocumentDefinitions> {
    const booking = this.data.booking;
    const customer = this.customerDetails || {};
    const vendor = this.vendorDetails || {};
    const equip = booking.equipment || {};

    // Read directly from Firestore-sourced vendorDetails — no fetch, no canvas.
    // Backward compatible: vendors without a converted letterhead simply have
    // letterheadBase64 === null/undefined here, and fall through to the
    // existing white quotation template below.
    const hasLetterhead = !!this.vendorDetails?.letterheadBase64;
    const vendorSigBase64 = this.vendorDetails?.signatureBase64 || null;
    const startDate = this.datePipe.transform(
      this.getBookingDate(booking.customer?.startDate || booking.startDate),
      'dd MMM yyyy',
    );
    const endDate = this.datePipe.transform(
      this.getBookingDate(booking.customer?.endDate || booking.endDate),
      'dd MMM yyyy',
    );
    const qDateStr = this.datePipe.transform(this.quotationDate, 'dd MMM yyyy');
    const refNo =
      'QT-' + (booking.id ? booking.id.slice(-6).toUpperCase() : 'XXXXXX');

    const digitalSigBlock: any[] = this.digitalSignature
      ? [
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: 515,
              y2: 0,
              lineWidth: 0.5,
              lineColor: '#e2e8f0',
            },
          ],
          margin: [0, 20, 0, 12],
        },
        {
          columns: [
            {
              stack: [
                { text: '🔒 DIGITALLY SIGNED', style: 'sealTitle' },
                {
                  text: `Signed By: ${this.digitalSignature.signedBy}`,
                  style: 'sealText',
                },
                {
                  text: `Date & Time: ${this.datePipe.transform(this.digitalSignature.signedAt, 'dd MMM yyyy, HH:mm')}`,
                  style: 'sealText',
                },
                {
                  text: `Reference: ${this.digitalSignature.refNo}`,
                  style: 'sealText',
                },
                {
                  text: `Integrity Hash: ${this.digitalSignature.hash}`,
                  style: 'sealHash',
                },
                {
                  text: 'This document was electronically signed via Aluguloo Platform',
                  style: 'sealFooter',
                  margin: [0, 6, 0, 0],
                },
              ],
              fillColor: '#f8fafc',
              border: [true, true, true, true],
              borderColor: ['#cbd5e1', '#cbd5e1', '#cbd5e1', '#cbd5e1'],
            },
          ],
        },
      ]
      : [];

    const consentBlock: any[] = this.consentRecord
      ? [
        {
          columns: [
            {
              stack: [
                {
                  text: '✔ VENDOR DECLARATION',
                  style: 'sealTitle',
                  margin: [8, 8, 8, 4],
                },
                {
                  text: `${this.consentRecord.vendorName} has confirmed that the details in this quotation are accurate and authorised its submission on ${this.datePipe.transform(this.consentRecord.agreedAt, 'dd MMM yyyy, HH:mm')}.`,
                  style: 'sealText',
                  margin: [8, 0, 8, 8],
                },
              ],
              fillColor: '#f0fdf4',
              border: [true, true, true, true],
              borderColor: ['#86efac', '#86efac', '#86efac', '#86efac'],
            },
          ],
          margin: [0, 8, 0, 0],
        },
      ]
      : [];

    // ── BACKGROUND ──────────────────────────────────────────────────────────
    // The base64 data URI comes straight from Firestore (vendorDetails.letterheadBase64,
    // generated server-side in convertLetterhead) and is accepted directly by
    // pdfMake — no external URL round-trip, no canvas, at render time.
    let background: any = undefined;
    if (hasLetterhead) {
      background = (pageSize: { width: number; height: number }) => ({
        image: this.vendorDetails.letterheadBase64, // already a data URI
        width: pageSize.width,
        height: pageSize.height,
        absolutePosition: { x: 0, y: 0 },
        opacity: 1,
      });
    }

    // ── HEADER BLOCK (suppressed when letterhead covers the branding area) ──
    const headerBlock: any[] = hasLetterhead
      ? []
      : [
        {
          columns: [
            vendor.logo
              ? { image: vendor.logo, width: 60, height: 60 }
              : { text: '', width: 60 },
            {
              stack: [
                {
                  text: (
                    vendor.companyName ||
                    vendor.name ||
                    'Vendor'
                  ).toUpperCase(),
                  style: 'headerCompany',
                },
                {
                  text: vendor.address || 'Vendor Address',
                  style: 'subHeader',
                },
                { text: `Phone: ${vendor.phone || '-'}`, style: 'subHeader' },
                { text: `Email: ${vendor.email || '-'}`, style: 'subHeader' },
              ],
              margin: [15, 0, 0, 0],
            },
          ].filter((x) => x !== null) as any,
        },
      ];

    return {
      background,
      pageMargins: hasLetterhead ? [40, 160, 40, 60] : [40, 40, 40, 60],
      content: [
        ...headerBlock,
        {
          text: 'QUOTATION',
          style: 'headerTitle',
          alignment: 'center',
          margin: [0, hasLetterhead ? 0 : 20, 0, 20],
        },
        {
          columns: [
            {
              stack: [
                { text: 'BILL TO', style: 'sectionTitle' },
                { text: customer.companyName || 'Customer Name', bold: true },
                { text: customer.companyDetails || 'Customer Address' },
                { text: `Phone: ${customer.phoneNumber || '-'}` },
                { text: `Email: ${customer.email || '-'}` },
              ].filter((x) => x !== null),
            },
            {
              stack: [
                {
                  text: 'QUOTATION DETAILS',
                  style: 'sectionTitle',
                  alignment: 'right',
                },
                { text: `Ref No: ${refNo}`, alignment: 'right' },
                { text: `Date: ${qDateStr || ''}`, alignment: 'right' },
                vendor.trn
                  ? { text: `TRN: ${vendor.trn}`, alignment: 'right' }
                  : null,
              ].filter((x) => x !== null) as any,
            },
          ],
        },
        {
          text: 'EQUIPMENT HIRE DETAILS',
          style: 'sectionTitle',
          margin: [0, 20, 0, 10],
        },
        booking.equipments && booking.equipments.length > 0
          ? {
            table: {
              widths: ['30%', '15%', '35%', '20%'],
              body: [
                [
                  { text: 'Equipment', bold: true, style: 'smallText' },
                  { text: 'Plate No', bold: true, style: 'smallText' },
                  { text: 'Duration', bold: true, style: 'smallText' },
                  { text: 'Location', bold: true, style: 'smallText' },
                ],
                ...booking.equipments.map((eq: any) => {
                  const eqStart = eq.startDate
                    ? eq.startDate.toDate
                      ? eq.startDate.toDate()
                      : new Date(eq.startDate)
                    : null;
                  const eqEnd = eq.endDate
                    ? eq.endDate.toDate
                      ? eq.endDate.toDate()
                      : new Date(eq.endDate)
                    : null;
                  const eqStartStr = eqStart
                    ? eqStart.toLocaleDateString('en-GB')
                    : '-';
                  const eqEndStr = eqEnd
                    ? eqEnd.toLocaleDateString('en-GB')
                    : '-';
                  return [
                    {
                      text:
                        eq.name || eq.customEquipmentName || '-',
                      style: 'smallText',
                    },
                    {
                      text:
                        eq.plate_number || eq.customPlateNumber || '-',
                      style: 'smallText',
                    },
                    {
                      text: `${eqStartStr} to ${eqEndStr}`,
                      style: 'smallText',
                    },
                    {
                      text: eq.location || eq.emirate || '-',
                      style: 'smallText',
                    },
                  ];
                }),
              ],
            },
            layout: 'lightHorizontalLines',
          }
          : {
            table: {
              widths: ['20%', '30%', '20%', '30%'],
              body: [
                [
                  { text: 'Equipment:', bold: true },
                  {
                    text: `${equip.name || '-'} (${equip.brand || ''} ${equip.model || ''})`,
                  },
                  { text: 'Plate No:', bold: true },
                  { text: equip.plate_number || '-' },
                ],
                [
                  { text: 'Duration:', bold: true },
                  {
                    text:
                      booking.customer?.priceType === 'per_trip'
                        ? `${startDate || ''} (${this.duration})`
                        : `${startDate || ''} to ${endDate || ''} (${this.duration})`,
                  },
                  { text: 'Location:', bold: true },
                  { text: booking.customer?.location || '-' },
                ],
              ],
            },
            layout: 'lightHorizontalLines',
          },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto'],
            body: (() => {
              const body: any[] = [
                [
                  { text: 'Description', style: 'tableHeader' },
                  { text: 'Rate / Type', style: 'tableHeader' },
                  {
                    text: 'Amount (AED)',
                    style: 'tableHeader',
                    alignment: 'right',
                  },
                ],
              ];

              if (booking.equipments && booking.equipments.length > 0) {
                booking.equipments.forEach((eq: any) => {
                  body.push([
                    {
                      text: `Rental Charges for ${eq.name || eq.customEquipmentName || 'Equipment'}`,
                      style: 'tableCell',
                    },
                    {
                      text: `AED ${this.decimalPipe.transform(eq.price, '1.0-0')} / fixed`,
                      style: 'tableCell',
                    },
                    {
                      text: this.decimalPipe.transform(eq.price, '1.2-2'),
                      style: 'tableCell',
                      alignment: 'right',
                    },
                  ]);
                });
              } else {
                body.push([
                  {
                    text: `Rental Charges for ${equip.name || 'Equipment'}`,
                  },
                  {
                    text: `AED ${this.totalAmount} / ${booking.customer?.priceType || '-'}`,
                  },
                  {
                    text:
                      this.decimalPipe.transform(
                        this.totalAmount,
                        '1.2-2',
                      ) || '0.00',
                    alignment: 'right' as any,
                  },
                ]);
              }

              if (this.deliveryCharge > 0) {
                body.push([
                  { text: 'Transport Cost', margin: [0, 5, 0, 0] },
                  { text: '' },
                  {
                    text: this.decimalPipe.transform(
                      this.deliveryCharge,
                      '1.2-2',
                    ),
                    alignment: 'right',
                    margin: [0, 5, 0, 0],
                  },
                ]);
              }

              body.push([
                {
                  text: 'TOTAL QUOTATION AMOUNT',
                  bold: true,
                  colSpan: 2,
                  margin: [0, 10, 0, 0],
                },
                {},
                {
                  text:
                    (this.decimalPipe.transform(
                      this.getFinalTotal(),
                      '1.2-2',
                    ) || '0.00') + ' AED',
                  bold: true,
                  alignment: 'right',
                  margin: [0, 10, 0, 0],
                  fontSize: 13,
                },
              ]);

              return body;
            })(),
          },
          margin: [0, 20, 0, 20],
          layout: {
            hLineWidth: (i: number, node: any) =>
              i === 0 || i === node.table.body.length ? 0 : 0.5,
            vLineWidth: () => 0,
            hLineColor: () => '#e5e7eb',
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 8,
            paddingBottom: () => 8,
          },
        },
        { text: 'Terms & Conditions', style: 'sectionTitle' },
        {
          ul: [
            'Rental period and rates are as specified above.',
            'Quotation is valid for 7 days from the date of issue.',
            'Equipment availability is subject to prior bookings.',
          ],
          style: 'smallText',
        },
        {
          columns: [
            {
              width: '50%',
              stack: [
                vendorSigBase64
                  ? {
                    image: vendorSigBase64,
                    width: 120,
                    margin: [0, 20, 0, 5],
                  }
                  : { text: '', margin: [0, 60, 0, 5] },
                {
                  canvas: [
                    {
                      type: 'line',
                      x1: 0,
                      y1: 0,
                      x2: 180,
                      y2: 0,
                      lineWidth: 1,
                    },
                  ],
                },
                {
                  text: 'Vendor Authorized Signature',
                  style: 'smallText',
                  margin: [0, 5, 0, 0],
                },
              ],
            },
          ] as any,
          margin: [0, 30, 0, 0],
        },
        ...digitalSigBlock,
        ...consentBlock,
      ],
      styles: {
        headerCompany: { fontSize: 18, bold: true, color: '#1d4ed8' },
        subHeader: { fontSize: 10, color: '#666' },
        headerTitle: { fontSize: 22, bold: true, color: '#1d4ed8' },
        sectionTitle: {
          fontSize: 12,
          bold: true,
          color: '#1d4ed8',
          margin: [0, 0, 0, 5],
        },
        tableHeader: {
          fontSize: 11,
          bold: true,
          fillColor: '#f3f4f6',
          margin: [5, 5, 5, 5],
        },
        smallText: { fontSize: 10, color: '#444' },
        sealTitle: {
          fontSize: 10,
          bold: true,
          color: '#1d4ed8',
          margin: [8, 8, 8, 4],
        },
        sealText: { fontSize: 9, color: '#334155', margin: [8, 2, 8, 2] },
        sealHash: {
          fontSize: 8,
          color: '#64748b',
          font: 'Courier',
          margin: [8, 2, 8, 2],
        },
        sealFooter: {
          fontSize: 8,
          color: '#94a3b8',
          italics: true,
          margin: [8, 0, 8, 8],
        },
      },
      defaultStyle: { fontSize: 11 },
    };
  }

  // ─────────────────────────────────────────────
  //  MISC / DIALOG
  // ─────────────────────────────────────────────

  close() {
    this.dialogRef.close();
  }

  triggerSigInput() {
    document.getElementById('quotationSigInput')?.click();
  }

  async onSigSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.ux.showOverlay('Uploading Signature...');
      try {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });

        const path = `signatures/vendor_${Date.now()}_${file.name}`;
        const storageRef = ref(this.storage, path);
        const uploadResult = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(uploadResult.ref);

        if (!this.vendorDetails) this.vendorDetails = {};
        this.vendorDetails.signatureUrl = url;
        this.vendorDetails.signatureBase64 = base64;
      } finally {
        this.ux.closeOverlay();
      }
    }
  }

  // ─────────────────────────────────────────────
  //  DRAW SIGNATURE METHODS
  // ─────────────────────────────────────────────

  initCanvas() {
    if (this.sigCanvas) {
      this.ctx = this.sigCanvas.nativeElement.getContext('2d')!;
      this.ctx.lineWidth = 2;
      this.ctx.lineCap = 'round';
      this.ctx.strokeStyle = '#000';
    }
  }

  startDrawing(event: MouseEvent) {
    if (!this.ctx) this.initCanvas();
    this.isDrawing = true;
    this.ctx.beginPath();
    this.ctx.moveTo(event.offsetX, event.offsetY);
  }

  draw(event: MouseEvent) {
    if (!this.isDrawing) return;
    this.ctx.lineTo(event.offsetX, event.offsetY);
    this.ctx.stroke();
  }

  stopDrawing() {
    this.isDrawing = false;
    this.ctx.closePath();
  }

  clearCanvas() {
    if (this.sigCanvas) {
      this.ctx.clearRect(
        0,
        0,
        this.sigCanvas.nativeElement.width,
        this.sigCanvas.nativeElement.height,
      );
    }
  }

  async saveCanvasSignature() {
    if (!this.sigCanvas) return;
    const canvas = this.sigCanvas.nativeElement;
    const base64Canvas = canvas.toDataURL('image/png');
    if (!this.vendorDetails) this.vendorDetails = {};
    this.vendorDetails.signatureBase64 = base64Canvas;

    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          const path = `signatures/vendor_drawn_${Date.now()}.png`;
          const storageRef = ref(this.storage, path);
          await uploadBytes(storageRef, blob);
          this.vendorDetails.signatureUrl = await getDownloadURL(storageRef);
        } catch (e) {
          console.error(e);
        }
      }
    }, 'image/png');
  }
}